import { defineStore } from 'pinia'
import { getCountMonthsByType, SubscriptionType } from '~/static/enums/user/subscription-type'
import { Generations, PaymentToSendEvent, UserAuth } from '~/static/types/user'
import { PaymentStatus } from '~/static/enums/payment-status'
import * as Sentry from '@sentry/vue'
import { PurchaseType, Purchases } from '~/static/enums/purchases'
import { GenerationsType } from '~/static/enums/generations-type'
import { useGlobals } from './global'

interface AuthState {
  user: UserAuth | null
  cuncelSubscribtionText: string
  cuncelSubscribtionTextError: string
  checkPaymentTimer: NodeJS.Timeout | null
  startTime: number | null
}

interface CreatePaymentResponse {
  new_payment: true
  payment_id: number
  pay_url: string | false
  cost: number
  purchase: string
}

export const useAuth = defineStore('auth', {
  state: (): AuthState => {
    return {
      user: null,
      cuncelSubscribtionText: '',
      cuncelSubscribtionTextError: '',
      checkPaymentTimer: null,
      startTime: null,
    }
  },
  actions: {
    async login(token: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const globals = useGlobals()
        globals.authToken = token
        this.getUser().then(() => {
          if (useRoute().path === '/') {
            useRouter().push('/files')
          }
          resolve()
        }).catch(() => {
          reject()
        })
      })
    },
    async getUser(): Promise<void> {
      const { response, error } = await useApi<UserAuth>('auth.getUser')
      const globals = useGlobals()
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        globals.authToken = null
      } else {
        this.user = response
        if (!process.server) {
          if (this.user.ati !== null && this.user.ati !== undefined) {
            globals.ati = this.user.ati
          } else {
            this.user.ati = <string>globals.ati
          }
        }
        return Promise.resolve()
      }

      return Promise.reject()
    },
    logout() {
      this.user = null
      const globals = useGlobals()
      globals.ati = null
      if (useRoute().path === '/cards' || useRoute().path === '/templates' || useRoute().path === '/files') {
        useRouter().push('/')
      }
    },
    async markDownload() {
      if (
        this.user === null ||
        (this.user.subscription_type === SubscriptionType.Free && this.user.remaining_downloads === 0)
      ) {
        return
      }
      const { response, error } = await useApi<boolean>('card.markDownload', {}, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        useNuxtApp().$event('popup:show', {
          text: 'Не удалось скачать карточку. Попробуйте позже',
        })
      } else if (response) {
        if (
          this.user.subscription_type === SubscriptionType.Free ||
            this.user.subscription_type === SubscriptionType.ExternalFree
        ) {
          this.user.remaining_downloads = (this.user?.remaining_downloads ?? 3) - 1
        }
        return true
      }
    },
    async createSubscription(
      type: SubscriptionType,
      withGenerations: boolean,
    ): Promise<CreatePaymentResponse | undefined> {
      if (this.user === null || this.user.subscription_type !== SubscriptionType.Free) {
        return
      }

      interface SubscriptionExistsResponse {
        new_payment: false
        expired_at: string
      }
      const { response, error } = await useApi<CreatePaymentResponse | SubscriptionExistsResponse>(
        'subscription.buy',
        {},
        HttpMethod.POST,
        {
          type: type,
          with_generations: withGenerations,
        },
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else {
        if (response.new_payment) {
          return response
        } else {
          this.user.subscription_expired_at = response.expired_at
        }
      }
      return undefined
    },
    async buyUpgradeSubscription(): Promise<CreatePaymentResponse | undefined> {
      if (
        this.user === null ||
        this.user.subscription_type === SubscriptionType.Free ||
        this.user.with_generations
      ) {
        return
      }

      interface CannotUpgradeResponse {
        new_payment: false
      }
      const { response, error } = await useApi<CreatePaymentResponse | CannotUpgradeResponse>(
        'subscription.upgrade',
        {},
        HttpMethod.POST,
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response.new_payment) {
        return response
      }
      return undefined
    },
    async buyGenerations(): Promise<CreatePaymentResponse | undefined> {
      if (this.user === null || this.user.subscription_type === SubscriptionType.Free) {
        return
      }

      const { response, error } = await useApi<CreatePaymentResponse>(
        'generations.buy',
        {},
        HttpMethod.POST,
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response.new_payment) {
        return response
      }
      return undefined
    },
    async cuncelSubscription() {
      if (this.user === null || this.user.subscription_type === SubscriptionType.Free || this.user.is_canceled) {
        return
      }

      this.cuncelSubscribtionTextError = new Validator(this.cuncelSubscribtionText)
        .required('Даже короткий отзыв будет полезен')
        .allErrorsToString()

      if (this.cuncelSubscribtionTextError !== '') {
        return false
      }

      const { error } = await useApi<true>('subscription.cancel', {}, HttpMethod.POST, {
        text: this.cuncelSubscribtionText,
      })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else {
        await this.getUser()
        return true
      }
    },
    initCheckPayment() {
      if (this.user === null) {
        return
      }
      this.user.payment_to_send_event.pending = {
        id: null,
        type: PurchaseType.MonthSubscription,
        date: '',
      }
      this.startCheckPayment()
    },
    async handlePaymentEvents(payments: PaymentToSendEvent[]) {
      for (const payment of payments) {
        Sentry.captureMessage('payment-' + payment.id, {
          extra: {
            message: 'Начата отправка событий для ранее созданного платежа.',
          },
        })
        await this.sendPaymentEvent(payment).then(async () => {
          await this.markEventIsSent((payment.id as number))
        })
      }
    },
    setGenerations(generations: Generations) {
      if (this.user) {
        this.user.generations = generations
      }
    },
    renewGenerations() {
      if (this.user === null) {
        return
      }
      this.user.generations.remaining_count += 100
      this.user.generations.total_count += 100
    },
    renewSubscription(type: SubscriptionType, withGenerations: boolean) {
      if (this.user === null) {
        return
      }
      this.user.subscription_type = type
      this.user.with_generations = withGenerations
      const subExpDate = new Date()
      subExpDate.setMonth(subExpDate.getMonth() + getCountMonthsByType(type))
      this.user.subscription_expired_at = subExpDate.getFullYear() +
        '-' + (subExpDate.getMonth() + 1) +
        '-' + subExpDate.getDate()
      if (!withGenerations) {
        this.user.generations.total_count = 5 + this.user.generations.total_count
        this.user.generations.remaining_count = 5 + this.user.generations.remaining_count
      } else {
        this.user.generations.total_count = 100 + this.user.generations.total_count
        this.user.generations.remaining_count = 100 + this.user.generations.remaining_count
      }
      this.user.generations.type = GenerationsType.Paid
      subExpDate.setMonth(new Date().getMonth() + 1)
      this.user.generations.expired_at = subExpDate.getFullYear() +
        '-' + (subExpDate.getMonth() + 1) +
        '-' + subExpDate.getDate()
    },
    upgradeSubscription() {
      if (this.user === null) {
        return
      }
      this.user.with_generations = true
      this.user.generations.total_count = 100 + this.user.generations.total_count
      this.user.generations.remaining_count = 100 + this.user.generations.remaining_count
    },
    async sendPaymentEvent(payment: PaymentToSendEvent) {
      const ym = (<{ ym?: unknown }>window).ym
      if (typeof ym === 'function') {
        Sentry.captureMessage('payment-' + payment.id, {
          extra: {
            message: 'Начата отправка события в яндекс.метрику для платежа.',
          },
        })
        ym(useRuntimeConfig().public.ymKey, 'reachGoal', Purchases[payment.type].ymEventName, {
          id: payment.id,
          date: payment.date,
        })
        Sentry.captureMessage('payment-' + payment.id, {
          extra: {
            message: 'Событие для платежа отправлено в яндекс.метрику.',
          },
        })
      }
      Sentry.captureMessage('payment-' + payment.id, {
        extra: {
          message: 'Начата отправка события в амплитуду для платежа.',
        },
      })
      Sentry.captureMessage('payment-' + payment.id, {
        extra: {
          message: 'Событие для платежа отправлено в амплитуду.',
        },
      })
    },
    async markEventIsSent(id: number) {
      await useApi<true>('payment.markEventIsSent', {}, HttpMethod.POST, {
        id,
      })
      Sentry.captureMessage('payment-' + id, {
        extra: {
          message: 'Платеж отмечен как отправленный в метрику и амплитуду.',
        },
      })
    },
    async checkPayment(id: number | null) {
      interface CheckPaymentResponse {
        status: PaymentStatus
        payment?: PaymentToSendEvent
      }

      const { response } = await useApi<CheckPaymentResponse>('payment.check', { id })
      if (response) {
        if (response.status === PaymentStatus.Confirmed) {
          Sentry.captureMessage('payment-' + id, {
            extra: {
              message: 'Начата отправка событий для платежа подтвержденного только что.',
            },
          })
          await this.sendPaymentEvent(response.payment as PaymentToSendEvent).then(async () => {
            await this.markEventIsSent(<number>(response.payment as PaymentToSendEvent).id)
          })
          this.stopCheckPayment()
        } else if (response.status !== PaymentStatus.Pending) {
          this.stopCheckPayment()
        } else if (((new Date().getTime()) - <number>this.startTime) > 20 * 60 * 1000) {
          this.stopCheckPayment()
        }
      } else {
        this.stopCheckPayment()
      }
    },
    async startCheckPayment() {
      if (this.user === null) {
        return
      }
      const payment = this.user.payment_to_send_event.pending
      if (payment === null) {
        return
      }
      if (this.checkPaymentTimer !== null) {
        clearInterval(this.checkPaymentTimer)
      }
      this.startTime = (new Date()).getTime()
      this.checkPaymentTimer = setInterval(this.checkPayment, 3000, payment.id)
    },
    stopCheckPayment() {
      clearInterval(<NodeJS.Timeout>this.checkPaymentTimer)
      this.checkPaymentTimer = null
    },
  },
  getters: {
    isAuth(state) {
      return state.user !== null && state.user !== undefined
    },
    isAdmin(state) {
      return state.user?.email === useRuntimeConfig().public.adminEmail
    },
  },
})
